import { useState } from "react";
import { motion } from "framer-motion";
import { REGIONS } from "@/lib/data";

export const CoverageMap = () => {
  const [active, setActive] = useState(REGIONS[1]); // OC default

  return (
    <section id="coverage" className="py-24 lg:py-40 bg-ink text-paper relative grain overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-[0.08] pointer-events-none" />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-primary-glow">Coverage</span>
            <h2 className="font-display text-3xl lg:text-5xl mt-3 leading-[1.05] tracking-[-0.02em] text-balance">
              Southern <em className="font-light text-paper/75">California.</em>
            </h2>
          </div>
          <p className="text-paper/70 max-w-md">Tap a jurisdiction to see RCCD's current and recent activity. Working relationships built shoulder-to-shoulder with city departments over decades.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Stylised map */}
          <div className="lg:col-span-7 relative aspect-[5/4] border border-paper/15 bg-ink/40">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
              {/* Stylized SoCal silhouette */}
              <path
                d="M5 22 L18 18 L28 24 L38 20 L46 28 L58 26 L70 32 L80 30 L92 42 L88 55 L82 68 L72 76 L60 84 L48 92 L38 88 L28 82 L20 70 L14 58 L10 44 Z"
                fill="hsl(36 28% 96% / 0.04)"
                stroke="hsl(36 28% 96% / 0.25)"
                strokeWidth="0.3"
              />
              {/* Coast line accent */}
              <path
                d="M5 22 L14 30 L20 42 L26 56 L34 68 L44 80 L48 92"
                fill="none"
                stroke="hsl(22 75% 55% / 0.5)"
                strokeWidth="0.4"
                strokeDasharray="1 1"
              />
              {/* Region markers */}
              {REGIONS.map(r => {
                const isActive = r.id === active.id;
                return (
                  <g key={r.id} onClick={() => setActive(r)} className="cursor-pointer">
                    <circle cx={r.x} cy={r.y} r={isActive ? 1.6 : 1.0}
                      fill={isActive ? 'hsl(22 88% 56%)' : 'hsl(220 18% 97%)'}
                      className="transition-all duration-500"
                    />
                    {isActive && (
                      <circle cx={r.x} cy={r.y} r="3" fill="none" stroke="hsl(22 88% 56%)" strokeWidth="0.2" opacity="0.6">
                        <animate attributeName="r" from="1.5" to="5" dur="2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" from="0.7" to="0" dur="2s" repeatCount="indefinite" />
                      </circle>
                    )}
                  </g>
                );
              })}
            </svg>
            <div className="absolute bottom-4 left-4 mono text-[10px] uppercase tracking-[0.2em] text-paper/40">
              Pacific Ocean ↙
            </div>
            <div className="absolute top-4 right-4 mono text-[10px] uppercase tracking-[0.2em] text-paper/40">
              N ↑
            </div>
          </div>

          {/* Region detail */}
          <div className="lg:col-span-5">
            <div className="flex flex-wrap gap-2 mb-8">
              {REGIONS.map(r => (
                <button
                  key={r.id}
                  onClick={() => setActive(r)}
                  className={`mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                    active.id === r.id
                      ? 'bg-paper text-ink border-paper'
                      : 'border-paper/20 text-paper/70 hover:border-paper/60'
                  }`}
                >
                  {r.name}
                </button>
              ))}
            </div>

            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-primary-glow">Active region</div>
              <h3 className="font-display text-4xl lg:text-5xl mt-3 tracking-[-0.02em]">{active.name}</h3>
              <div className="mt-8 grid grid-cols-2 gap-6 border-t border-paper/15 pt-6">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-paper/55">Activity</div>
                  <div className="font-display text-3xl lg:text-4xl text-paper mt-2 leading-tight">{active.projects}</div>
                </div>
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-paper/55">Footprint</div>
                  <div className="font-display text-3xl lg:text-4xl text-paper mt-2 leading-tight">{active.acres}</div>
                </div>
              </div>
              <p className="mt-8 text-paper/75 max-w-md leading-relaxed">
                Working relationships with City Managers, Community Development Directors, and Planning, Engineering, Building, Finance, and Utilities departments — built over decades of shoulder-to-shoulder work.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
